import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'web-library/stories/atom/Box/Box';
import Link from 'web-library/stories/atom/Link/Link';
import Typography from 'web-library/stories/atom/Typography/Typography';
import WidthGuard, { useWithGuardStyles } from 'web-library/stories/atom/WidthGuard';

import { typeTheme, typographyTheme } from 'web-library/theme/helper/tssHelper';

const flexStart = 'flex-start';
const useStyles = makeStyles()((theme) => ({
  footer: {
    zIndex: 3,
  },
  contentPadding: {
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(7),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  supportRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: theme.color.background.bgInverse01,
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: flexStart,
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
  footerDivider: {
    borderWidth: 0,
    borderBottomWidth: 1,
    borderStyle: 'solid',
    width: '100%',
    borderColor: theme.color.border.borderInverse,
  },
  brandName: {
    ...typographyTheme(theme, 'h3'),
    color: theme.color.text.textContrast,
    marginBottom: theme.spacing(3),
    maxWidth: '575px',
  },
  brandNameSmallerSize: {
    ...typographyTheme(theme, 'h5'),
    [theme.breakpoints.down('md')]: {
      ...typographyTheme(theme, 'h4'),
      paddingBottom: theme.spacing(2),
    },
    color: theme.color.text.textContrast,
  },
  supportLinks: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      justifyContent: flexStart,
      paddingTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      flexDirection: 'column',
      alignItems: flexStart,
    },
  },
  lastItem: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      flexDirection: 'column',
      justifyContent: flexStart,
      paddingTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0),
      flexDirection: 'column',
      alignItems: flexStart,
    },
  },
  supportLink: {
    display: 'flex',
    flexDirection: 'column',
  },
  supportUrl: {
    ...typeTheme(theme, 'body1ExtraBold'),
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      width: '214px',
    },
  },
  policyContainer: {
    width: '100%',
    backgroundColor: theme.color.background.bgInverse01,
  },
  policyRow: {
    flex: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    justifyContent: flexStart,
    backgroundColor: theme.color.background.bgInverse01,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  headerLabel: {
    marginTop: theme.spacing(4),
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  policyLinks: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: flexStart,
    alignItems: flexStart,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  policyUrl: {
    ...typeTheme(theme, 'body1ExtraBold'),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
      paddingBottom: theme.spacing(1),
      width: '214px',
    },
  },
  linkContainer: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(0),
    },
    maxWidth: '214px',
    [theme.breakpoints.down('sm')]: {
      width: '214px',
    },
  },
  copyRight: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: flexStart,
    alignItems: 'flex-end',
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(1),
      alignItems: flexStart,
    },
  },
  copyRightText: {
    color: theme.color.text.textContrast,
  },
  optimizedFor: {
    color: theme.color.text.textContrast,
    fontSize: '14px',
    textAlign: 'center',
    paddingBottom: theme.spacing(3),
  },
}));

const Footer = () => {
  const { classes } = useStyles();
  const { classes: guardClasses } = useWithGuardStyles();
  const theme = useTheme();

  const { t } = useTranslation();

  return (
    <Box className={classes.footer}>
      <WidthGuard backgroundColor={theme.color.background.bgInverse01}>
        <Box className={[guardClasses.container].join(' ')}>
          <Box className={[classes.headerLabel, classes.contentPadding].join(' ')}>
            <Link
              className={classes.brandName}
              href={t('helpdesk->>Links.SEAB Site')}
              target="_blank"
              rel="noopener,noreferrer"
              contrast
              footer
              withUnderline={false}
            >
              Singapore Examinations and Assessment Board
            </Link>
            <Box className={classes.supportLinks}>
              <Link
                className={classes.supportUrl}
                contrast
                footer
                href={t('helpdesk->>Links.SEAB Contact Us')}
                target="_blank"
                rel="noopener,noreferrer"
              >
                Contact Us
              </Link>
              <Link className={classes.supportUrl} contrast footer href="/faq">
                FAQ
              </Link>
            </Box>
          </Box>

          <Box className={[classes.policyContainer, classes.contentPadding].join(' ')}>
            <Box className={classes.footerDivider}>
              <></>
            </Box>

            <Box className={classes.policyRow}>
              <Box className={classes.policyLinks}>
                <Link className={classes.policyUrl} contrast footer href="/privacy-statement">
                  Privacy Statement
                </Link>
                <Link
                  className={classes.policyUrl}
                  contrast
                  footer
                  href={t('helpdesk->>Links.Report Vulnerability')}
                  target="_blank"
                  rel="noopener,noreferrer"
                >
                  Report Vulnerability
                </Link>
                <Link className={classes.policyUrl} contrast footer href="/terms-of-use">
                  Terms of Use
                </Link>
              </Box>
              <Box className={classes.copyRight}>
                <Typography className={classes.copyRightText} variant="body2">
                  &copy; 2026 Singapore Examinations and Assessment Board
                </Typography>
                <Typography className={classes.copyRightText} variant="body2">
                  Last Updated on 29 Jul 2026
                </Typography>
              </Box>
            </Box>
            <Box className={classes.optimizedFor}>
              This website is optimised for Edge, Chrome, Safari.
            </Box>
          </Box>
        </Box>
      </WidthGuard>
    </Box>
  );
};

export default Footer;
